import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import { formatSiteDate, formatSiteDateTime, isCalendarDateOnly } from '../domain/siteTime/siteTime';
import { customerFacingClaimReadinessLabel, resolveConfiguredZoneLabel } from '../domain/customerLabels/customerFacingControlLabel';
import { useAuth } from '../contexts/AuthContext';
import { canAssignSafetyCaseOwner, canManageSafetyCaseArtifacts } from '../auth/access';
import { api } from '../services/api';
import {
  reportsApi,
  type ClaimReadinessControlSummary,
  type ObservationDispositionQueueResponse,
  type OperatorLifecycleResponse,
  type SafetyCaseOpenResponse,
  type SafetyCaseRecord,
  type CaseTimelineEvent,
  type WorkflowClaimReadinessResponse,
} from '../services/reportsApi';
import { readPersistedReportWindow } from '../domain/acceptance/safetyScanReportContext';
import {
  buildScanVerifyPath,
  formatHumanScanWindow,
} from '../domain/acceptance/scanVerifyNavigation';
import { hasNewActivitySinceSignedScan } from '../domain/safetyCases/safetyCaseWorklist';
import {
  claimReadinessFocusOpensMore,
  hasRecordedSafetyCaseControl,
  parseOperatorLifecycle,
  resolveOverviewCtaFromLifecycle,
  resolveSafetyCaseDetailTabFromSearch,
  resolveSafetyCaseDominantCta,
  buildSafetyManagementWorkspaceHref,
  SAFETY_CASE_DETAIL_MAX_WIDTH_PX,
  SAFETY_CASE_DETAIL_TABS,
  type SafetyCaseDetailTab,
} from '../domain/safetyCases/safetyCaseDetailTabs';
import {
  operatorVisibleNote,
  selectPrimaryControlRecommendation,
} from '../domain/safetyCases/safetyCaseControlsPresenter';
import { buildSafetyCaseControlsPath } from '../domain/safetyCases/investigateControlLink';
import {
  resolveCaseSignedRecordIdentity,
  type SignedRecordRevisionSource,
} from '../domain/safetyCases/workflowGlueNavigation';
import {
  buildOverviewPrepareClaimReadinessPath,
  buildExactSignedRecordPath,
  buildSafetyCasePatternReviewPath,
  buildSafetyOverviewV2Path,
  formatOverviewLatestEvidence,
  formatOverviewProblemSummary,
  formatOverviewProblemTitle,
  resolveOverviewControlView,
  resolveOverviewEvidenceIncidentId,
  resolveOverviewFollowUp,
  resolveOverviewNextStep,
  resolveNextSafetyScanView,
  resolveOverviewPlaceLabel,
  resolveScanRepresentativeIncidentId,
} from '../domain/safetyCases/safetyCaseOverviewPresenter';
import { presentMeasurementReferences } from '../domain/safetyCases/measurementReferencesPresenter';
import { useCaseHomeFirstScanFinding } from '../domain/safetyCases/useFirstScanFinding';
import { resolveActiveDraftScanWindow } from '../domain/safetyCases/safetyCaseHomeStatus';
import { mapBuyerCaseState } from '../domain/safetyCases/managedSafetyBuyerUx';
import { resolveBuyerCustomerScanDownloadReportId } from '../domain/safetyCases/scanBuyerResultHierarchy';
import { mapControlStage } from '../domain/safetyCases/workspaceResolvedState';
import { ClaimReadinessValidationSection } from './ClaimReadinessValidationSection';
import { SafetyCaseHomeStatusStrip } from '../components/safetyReports/SafetyCaseHomeStatusStrip';
import { FirstSafetyScanFindingCard } from '../components/safetyReports/FirstSafetyScanFindingCard';
import { SafetyCaseOwnershipSection } from '../components/safetyReports/SafetyCaseOwnershipSection';
import { useCaseScheduledScanFacts } from '../domain/safetyCases/useCaseScheduledScanFacts';
import { SafetyCaseOverviewPanels } from './SafetyCaseOverviewPanels';
import SafetyCaseRecommendationsSection from './SafetyCaseRecommendationsSection';
import WorkplaceConditionSummaryCard from '../components/safetyCases/WorkplaceConditionSummaryCard';
import ContributingConditionReviewSection, {
  type ContributingConditionPreview,
} from '../components/safetyCases/ContributingConditionReviewSection';
import ClosureSummarySection from '../components/safetyCases/ClosureSummarySection';
import FollowUpResultSection from '../components/safetyCases/FollowUpResultSection';
import ControlEvaluationPanel from '../components/safetyCases/ControlEvaluationPanel';
import { CaseHistorySection } from '../components/safetyCases/CaseHistoryPanel';
import { CaseVerifyPanel } from '../components/safetyCases/CaseScanRegister';
import SafetyCaseAttestationSaveBar from '../components/safetyCases/SafetyCaseAttestationSaveBar';
import {
  runSafetyCaseAttestationSave,
  type SafetyCaseAttestationSaveHandler,
} from '../domain/safetyCases/safetyCaseAttestationSave';
import {
  deriveResultLabel,
  resolveResultLabelDisplay,
} from '../domain/safetyCaseClosure/safetyCaseClosure';
import { readWorkplaceCondition, resolveContributingConditionStatusLabel } from '../domain/workplaceCondition/workplaceCondition';
import {
  buildClaimReadinessBlockerReviewPath,
  claimReadinessBlockerActionLabel,
  resolveClaimReadinessBlockerReviewFocus,
} from '../domain/incidents/riskPatternWorkflowReviewContext';

function humanize(value: unknown, fallback = 'Not recorded'): string {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value?: string | null, fallback = 'Not recorded', timeZone?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return fallback;
  if (isCalendarDateOnly(text)) return formatSiteDate(text, timeZone) || text;
  return formatSiteDateTime(text, timeZone) || text;
}

function firstZone(input?: SafetyCaseRecord | null): string {
  const zones = Array.isArray(input?.scope?.zones) ? input?.scope?.zones as unknown[] : [];
  const raw = String(zones[0] || input?.scope?.zone_id || '').trim();
  if (!raw) return 'Unspecified zone';
  return resolveConfiguredZoneLabel(raw) || raw;
}

function firstRisk(input?: SafetyCaseRecord | null): string {
  return String(input?.scope?.risk_type || input?.hazard_category || input?.finding_type || 'Safety case');
}

function controlStatusLabel(status?: string | null): string {
  const key = String(status || '').trim().toLowerCase();
  if (key === 'resolved') return 'Resolved';
  if (key === 'not_applicable') return 'Not applicable';
  if (key === 'needs_attestation') return 'Needs attestation';
  if (key === 'blocked') return 'Blocked';
  return humanize(status, 'Pending');
}

function controlStatusColor(status?: string | null): 'success' | 'warning' | 'error' | 'default' {
  const key = String(status || '').trim().toLowerCase();
  if (key === 'resolved' || key === 'not_applicable') return 'success';
  if (key === 'blocked') return 'error';
  if (key === 'needs_attestation') return 'warning';
  return 'default';
}

function isUnresolvedBlockingControl(control: ClaimReadinessControlSummary): boolean {
  if (control.blocking === false) return false;
  const status = String(control.status || '').trim().toLowerCase();
  return Boolean(status && status !== 'resolved' && status !== 'not_applicable');
}

async function loadCaseTimelineEvents(safetyCaseId: string): Promise<CaseTimelineEvent[]> {
  try {
    const timeline = await reportsApi.listCaseTimeline(safetyCaseId);
    return Array.isArray(timeline.events) ? timeline.events : [];
  } catch {
    return [];
  }
}

const SafetyCaseDetailPage: React.FC = () => {
  const { caseId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = resolveSafetyCaseDetailTabFromSearch(searchParams);
  const setActiveTab = useCallback((next: SafetyCaseDetailTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'overview') nextParams.delete('tab');
    else nextParams.set('tab', next);
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);
  const { effectiveUser: user } = useAuth();
  const canManageSafetyCases = canManageSafetyCaseArtifacts(user);
  const canAssignOwner = canAssignSafetyCaseOwner(user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<SafetyCaseOpenResponse | null>(null);
  const [workflowClaimReadinessState, setWorkflowClaimReadinessState] = useState<WorkflowClaimReadinessResponse | null>(null);
  const [contributingPreview, setContributingPreview] = useState<ContributingConditionPreview>({
    likelyContributingCondition: '',
    contributingConditionStatus: '',
  });
  const contributingConditionReviewRef = useRef<HTMLDivElement | null>(null);
  const closureSummaryRef = useRef<HTMLDivElement | null>(null);
  const workplaceConditionSaveRef = useRef<SafetyCaseAttestationSaveHandler | null>(null);
  const contributingConditionSaveRef = useRef<SafetyCaseAttestationSaveHandler | null>(null);
  const closureSummarySaveRef = useRef<SafetyCaseAttestationSaveHandler | null>(null);
  const followUpResultSaveRef = useRef<SafetyCaseAttestationSaveHandler | null>(null);
  const [attestationSaving, setAttestationSaving] = useState(false);
  const [attestationSaveError, setAttestationSaveError] = useState<string | null>(null);
  const [attestationSaveNotice, setAttestationSaveNotice] = useState<string | null>(null);
  const [moreSection, setMoreSection] = useState<'condition' | 'claim' | 'attestation' | 'audit'>('condition');
  const [timelineEvents, setTimelineEvents] = useState<CaseTimelineEvent[]>([]);
  const useUnifiedAttestationSave = canManageSafetyCases;

  const loadCase = useCallback(async (targetCaseId: string, options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const response = await reportsApi.openSafetyCase(targetCaseId);
      setPayload(response);
      setTimelineEvents(await loadCaseTimelineEvents(targetCaseId));
    } catch (err: any) {
      setPayload(null);
      setTimelineEvents([]);
      setError(err?.message || 'Safety Case details are unavailable right now.');
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, []);

  const handleSaveAllAttestation = useCallback(async () => {
    setAttestationSaving(true);
    setAttestationSaveError(null);
    setAttestationSaveNotice(null);
    try {
      await runSafetyCaseAttestationSave([
        workplaceConditionSaveRef.current,
        contributingConditionSaveRef.current,
        closureSummarySaveRef.current,
        followUpResultSaveRef.current,
      ]);
      setAttestationSaveNotice('Safety Case attestation saved.');
      await loadCase(caseId);
    } catch (err: any) {
      setAttestationSaveError(err?.message || 'Unable to save Safety Case attestation right now.');
    } finally {
      setAttestationSaving(false);
    }
  }, [caseId, loadCase]);

  useEffect(() => {
    let cancelled = false;
    setWorkflowClaimReadinessState(null);
    async function run() {
      try {
        setLoading(true);
        setError(null);
        const response = await reportsApi.openSafetyCase(caseId);
        if (!cancelled) setPayload(response);
        const events = await loadCaseTimelineEvents(caseId);
        if (!cancelled) setTimelineEvents(events);
      } catch (err: any) {
        if (!cancelled) {
          setPayload(null);
          setError(err?.message || 'Safety Case details are unavailable right now.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (!caseId) {
      setLoading(false);
      setError('Safety Case id is missing.');
      return undefined;
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const payloadMatchesCase = useMemo(() => {
    if (!caseId || !payload) return !caseId;
    const record = payload.safety_case || payload.finding;
    if (!record) return false;
    const recordCaseId = String(record.safety_case_id || record.finding_id || '').trim();
    return !recordCaseId || recordCaseId === caseId;
  }, [caseId, payload]);
  const item = useMemo(() => {
    const record = payload?.safety_case || payload?.finding || null;
    if (!record) return null;
    const recordCaseId = String(record.safety_case_id || record.finding_id || '').trim();
    if (caseId && recordCaseId && recordCaseId !== caseId) return null;
    return record;
  }, [caseId, payload]);
  const siteTimeZone = useSiteTimeZone(String(item?.site_id || searchParams.get('site_id') || '').trim() || null);
  const workflowSummary = useMemo(() => {
    if (!item) return null;
    return payload?.workflow_summary || null;
  }, [item, payload?.workflow_summary]);
  const workflowId = useMemo(
    () => String(payload?.workflow_id || item?.workflow_id || '').trim(),
    [item?.workflow_id, payload?.workflow_id],
  );
  const scheduledScanFacts = useCaseScheduledScanFacts(
    item?.site_id || searchParams.get('site_id'),
    workflowId,
  );
  const draftReportId = useMemo(
    () => String(item?.active_draft_report_id || '').trim() || null,
    [item?.active_draft_report_id],
  );
  const buyerCustomerScanReportId = useMemo(() => {
    const history = (
      ((item as { scan_history?: unknown } | null)?.scan_history
        || (workflowSummary as { scan_history?: unknown } | null)?.scan_history
        || []) as Array<Record<string, unknown>>
    );
    return resolveBuyerCustomerScanDownloadReportId({
      openedReportId: draftReportId,
      scanHistory: history,
      latestSignedReportId: item?.latest_finalized_report_id,
    }) || draftReportId;
  }, [draftReportId, item, workflowSummary]);
  const claimReadinessScopeKey = useMemo(
    () => `${caseId}:${workflowId}:${draftReportId || ''}:${item?.updated_at || ''}`,
    [caseId, draftReportId, item?.updated_at, workflowId],
  );

  useEffect(() => {
    setWorkflowClaimReadinessState(null);
    if (!workflowId) return undefined;

    let cancelled = false;
    void (async () => {
      try {
        const response = await reportsApi.getWorkflowClaimReadiness(workflowId, draftReportId);
        if (cancelled) return;
        if (response?.ok) {
          setWorkflowClaimReadinessState(response);
        } else {
          setWorkflowClaimReadinessState(null);
        }
      } catch {
        if (!cancelled) setWorkflowClaimReadinessState(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [claimReadinessScopeKey, draftReportId, workflowId]);

  const projectedWorkflowClaimReadiness = useMemo(() => {
    const projected = workflowSummary?.claim_readiness;
    return projected && typeof projected === 'object'
      ? ({ ok: true, ...projected } as WorkflowClaimReadinessResponse)
      : null;
  }, [workflowSummary?.claim_readiness]);

  const effectiveWorkflowClaimReadiness = useMemo<WorkflowClaimReadinessResponse | null>(() => {
    const liveWorkflowId = String(workflowClaimReadinessState?.workflow_id || workflowId || '').trim();
    const liveMatchesScope = !workflowClaimReadinessState || liveWorkflowId === workflowId;
    const liveHasControls = Boolean(
      workflowClaimReadinessState
      && liveMatchesScope
      && (
        Array.isArray(workflowClaimReadinessState.controls)
        || typeof workflowClaimReadinessState.total_count === 'number'
      ),
    );
    if (liveHasControls) return workflowClaimReadinessState;
    return projectedWorkflowClaimReadiness;
  }, [projectedWorkflowClaimReadiness, workflowClaimReadinessState, workflowId]);

  const claimReadinessControls = useMemo<ClaimReadinessControlSummary[]>(
    () => (Array.isArray(effectiveWorkflowClaimReadiness?.controls) ? effectiveWorkflowClaimReadiness?.controls || [] : []),
    [effectiveWorkflowClaimReadiness],
  );
  const hasCanonicalClaimReadiness = useMemo(
    () => claimReadinessControls.length > 0 || Number(effectiveWorkflowClaimReadiness?.total_count || 0) > 0,
    [claimReadinessControls.length, effectiveWorkflowClaimReadiness?.total_count],
  );
  const claimReadinessSummaryLabel = useMemo(() => {
    if (!hasCanonicalClaimReadiness) return '';
    const resolvedCount = Number(effectiveWorkflowClaimReadiness?.resolved_count || 0);
    const totalCount = Number(
      effectiveWorkflowClaimReadiness?.total_count
      || claimReadinessControls.length
      || 0,
    );
    if (!totalCount) return '';
    return `Claim readiness: ${resolvedCount}/${totalCount} resolved`;
  }, [
    claimReadinessControls.length,
    effectiveWorkflowClaimReadiness?.resolved_count,
    effectiveWorkflowClaimReadiness?.total_count,
    hasCanonicalClaimReadiness,
  ]);
  const unresolvedBlockingControls = useMemo(() => {
    if (!hasCanonicalClaimReadiness) return [];
    return claimReadinessControls.filter(isUnresolvedBlockingControl);
  }, [claimReadinessControls, hasCanonicalClaimReadiness]);

  const focusClaimReadinessBlocker = useCallback((blockerType: string) => {
    const type = String(blockerType || '').trim().toLowerCase();
    const reviewFocus = resolveClaimReadinessBlockerReviewFocus(type);
    if (reviewFocus && item) {
      const siteId = String(item.site_id || searchParams.get('site_id') || '').trim();
      if (siteId) {
        const scope = (item.scope && typeof item.scope === 'object') ? item.scope : {};
        const zones = Array.isArray(scope.zones) ? scope.zones : [];
        const incidentId = String(
          payload?.representative_incident_id
          || item.representative_incident_id
          || searchParams.get('incident_id')
          || '',
        ).trim();
        navigate(buildClaimReadinessBlockerReviewPath({
          siteId,
          workflowId,
          incidentId: incidentId || undefined,
          zoneId: String(zones[0] || scope.zone_id || '').trim() || undefined,
          cameraId: String(scope.camera_id || '').trim() || undefined,
          deviceId: String(scope.device_id || '').trim() || undefined,
          riskType: String(scope.risk_type || item.hazard_category || item.finding_type || '').trim() || undefined,
          reviewFocus,
        }));
        return;
      }
    }
    if (claimReadinessFocusOpensMore(type)) {
      setActiveTab('more');
      setMoreSection(type === 'closure_summary_required' ? 'attestation' : 'condition');
      window.setTimeout(() => {
        if (type === 'contributing_condition_status_required') {
          contributingConditionReviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (type === 'closure_summary_required') {
          closureSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    }
  }, [item, navigate, payload?.representative_incident_id, searchParams, setActiveTab, workflowId]);

  useEffect(() => {
    if (loading || !item) return undefined;
    const focus = String(searchParams.get('claim_readiness_focus') || '').trim().toLowerCase();
    if (!focus) return undefined;
    const timer = window.setTimeout(() => {
      focusClaimReadinessBlocker(focus);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [focusClaimReadinessBlocker, item, loading, searchParams]);

  useEffect(() => {
    if (activeTab !== 'more') return;
    if (String(searchParams.get('section') || '').trim().toLowerCase() !== 'claim') return;
    setMoreSection('claim');
  }, [activeTab, searchParams]);
  const [verifyPendingCount, setVerifyPendingCount] = useState<number | null>(null);
  const [scanProgress, setScanProgress] = useState<ObservationDispositionQueueResponse['progress'] | null>(null);
  const [scanQueueWindow, setScanQueueWindow] = useState<{ start?: string | null; end?: string | null } | null>(null);
  const [scanReportAnchorWindow, setScanReportAnchorWindow] = useState<{ start?: string | null; end?: string | null } | null>(null);
  const [scanLocationLabel, setScanLocationLabel] = useState<string | null>(null);
  const [scanQueueIncidentId, setScanQueueIncidentId] = useState<string | null>(null);
  const [scanRepresentativeIncidentId, setScanRepresentativeIncidentId] = useState<string | null>(null);
  const [scanCameraId, setScanCameraId] = useState<string | null>(null);
  const [evidenceImageUrl, setEvidenceImageUrl] = useState<string | null>(null);
  const evidenceImageUrlRef = useRef<string | null>(null);
  const [operatorLifecycle, setOperatorLifecycle] = useState<OperatorLifecycleResponse | null>(null);
  const [lifecycleReadyFor, setLifecycleReadyFor] = useState<string | null>(null);
  useEffect(() => {
    const draftReportId = String(item?.active_draft_report_id || '').trim();
    if (!draftReportId) {
      setVerifyPendingCount(null);
      setScanProgress(null);
      setScanQueueWindow(null);
      setScanReportAnchorWindow(null);
      setScanLocationLabel(null);
      setScanQueueIncidentId(null);
      setScanRepresentativeIncidentId(null);
      setScanCameraId(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [queue, reportOpen] = await Promise.all([
          reportsApi.getObservationDispositionQueue(draftReportId, { limit: 1 }).catch(() => null),
          reportsApi.openSafetyReport(draftReportId).catch(() => null),
        ]);
        if (cancelled) return;
        const progress = queue?.progress || null;
        setScanProgress(progress);
        setScanQueueWindow(queue?.window || null);
        const openedReport = reportOpen && typeof reportOpen === 'object'
          ? ((reportOpen as { report?: unknown }).report || reportOpen)
          : null;
        const fromOpen = readPersistedReportWindow(openedReport);
        setScanReportAnchorWindow(fromOpen.start && fromOpen.end ? fromOpen : null);
        setScanLocationLabel(String(queue?.location_label || '').trim() || null);
        setScanCameraId(String(queue?.scope?.camera_id || '').trim() || null);
        setScanQueueIncidentId(
          String(queue?.items?.[0]?.incident_id || queue?.next_incident_id || '').trim() || null,
        );
        setScanRepresentativeIncidentId(resolveScanRepresentativeIncidentId(reportOpen) || null);
        const pending = Number((progress as { unreviewed?: number } | null | undefined)?.unreviewed);
        setVerifyPendingCount(Number.isFinite(pending) ? pending : null);
      } catch {
        if (!cancelled) {
          setScanProgress(null);
          setScanQueueWindow(null);
          setScanReportAnchorWindow(null);
          setScanLocationLabel(null);
          setScanQueueIncidentId(null);
          setScanRepresentativeIncidentId(null);
          setScanCameraId(null);
          setVerifyPendingCount(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.active_draft_report_id]);

  const { view: firstScanFinding, count: logicalScanCount } = useCaseHomeFirstScanFinding({
    item, workflowSummary: workflowSummary as Record<string, unknown> | null, workflowId, searchParams,
    scanQueueWindow, scanReportAnchorWindow, scanLocationLabel, scanCameraId, siteTimeZone,
    operatorLifecycle, scanProgress, riskType: item ? firstRisk(item) : null,
  });

  const evidenceIncidentId = resolveOverviewEvidenceIncidentId({
    scanRepresentativeIncidentId,
    representativeIncidentId: payload?.representative_incident_id || item?.representative_incident_id,
    queueIncidentId: scanQueueIncidentId,
  });

  useEffect(() => {
    let cancelled = false;
    const revoke = () => {
      if (evidenceImageUrlRef.current) {
        URL.revokeObjectURL(evidenceImageUrlRef.current);
        evidenceImageUrlRef.current = null;
      }
    };
    revoke();
    setEvidenceImageUrl(null);
    if (!evidenceIncidentId) {
      return () => {
        cancelled = true;
        revoke();
      };
    }
    void (async () => {
      try {
        const res = await api.get(
          `/api/v1/incidents/${encodeURIComponent(evidenceIncidentId)}/evidence/latest/snapshot`,
          { responseType: 'blob' },
        );
        const blob = res.data as Blob;
        if (!blob || blob.size < 32) return;
        if (blob.type && !String(blob.type).startsWith('image/')) return;
        const objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        evidenceImageUrlRef.current = objectUrl;
        setEvidenceImageUrl(objectUrl);
      } catch {
        if (!cancelled) setEvidenceImageUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      revoke();
    };
  }, [evidenceIncidentId]);

  const lifecycleReportId = useMemo(
    () => String(item?.active_draft_report_id || item?.latest_finalized_report_id || '').trim() || null,
    [item?.active_draft_report_id, item?.latest_finalized_report_id],
  );

  useEffect(() => {
    const requestKey = lifecycleReportId || '';
    if (!lifecycleReportId) {
      setOperatorLifecycle(null);
      setLifecycleReadyFor('');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await reportsApi.getOperatorLifecycle(lifecycleReportId);
        if (cancelled) return;
        setOperatorLifecycle(response?.ok === false ? null : response);
      } catch {
        if (!cancelled) setOperatorLifecycle(null);
      } finally {
        if (!cancelled) setLifecycleReadyFor(requestKey);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lifecycleReportId]);
  const operatorLifecycleReady = lifecycleReadyFor === (lifecycleReportId || '');

  const openSelectedScan = useCallback((reportId?: string) => {
    if (!item) return;
    const selectedReportId = String(
      reportId || buyerCustomerScanReportId || item.active_draft_report_id || '',
    ).trim();
    if (!selectedReportId) return;
    navigate(buildScanVerifyPath({
      reportId: selectedReportId,
      orgId: searchParams.get('org_id') || item.org_id,
      siteId: String(item.site_id || searchParams.get('site_id') || '').trim() || null,
      safetyCaseId: String(item.safety_case_id || item.finding_id || caseId || '').trim() || null,
    }));
  }, [buyerCustomerScanReportId, caseId, item, navigate, searchParams]);
  const openVerifyObservations = useCallback(() => {
    openSelectedScan();
  }, [openSelectedScan]);

  const establishMeasurementReference = useCallback(async (
    role: 'initial_baseline' | 'control_reference',
  ) => {
    if (!lifecycleReportId) return;
    try {
      const response = await reportsApi.establishMeasurementReference(lifecycleReportId, role);
      if (response && response.ok !== false) setOperatorLifecycle(response);
      else setError('Baseline establishment could not be started.');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message;
      setError(String(detail || 'Baseline establishment could not be started.').trim());
    }
  }, [lifecycleReportId]);

  const openPreviousScan = useCallback(() => {
    if (!item) return;
    const previousReportId = String(item.previous_report_id || '').trim();
    if (!previousReportId) return;
    openSelectedScan(previousReportId);
  }, [item, openSelectedScan]);

  const viewSignedScan = useCallback((reportId?: string | null) => {
    if (!item) return;
    const selected = String(reportId || item.latest_finalized_report_id || '').trim();
    if (!selected) return;
    const latestFinalized = String(item.latest_finalized_report_id || '').trim();
    const identity = resolveCaseSignedRecordIdentity({
      reportId: selected,
      finalizedReportId: latestFinalized || selected,
      latestScanCurrentRevision: selected === latestFinalized
        ? ((item as { latest_scan_current_revision?: unknown }).latest_scan_current_revision
          ?? (workflowSummary as { latest_scan_current_revision?: unknown } | null)?.latest_scan_current_revision)
        : undefined,
      scanHistory: (
        (item as { scan_history?: SignedRecordRevisionSource['scanHistory'] }).scan_history
        || (workflowSummary as { scan_history?: SignedRecordRevisionSource['scanHistory'] } | null)?.scan_history
        || null
      ),
    });
    const href = buildExactSignedRecordPath({
      recordId: identity.recordId,
      revision: identity.revision,
      orgId: searchParams.get('org_id'),
      siteId: String(item.site_id || searchParams.get('site_id') || '').trim() || null,
    });
    if (!href) return;
    navigate(href);
  }, [item, navigate, searchParams, workflowSummary]);

  const actions = useMemo(() => {
    const summaryActions = workflowSummary?.corrective_actions || {};
    return [
      ...((summaryActions.active_actions as Array<Record<string, any>> | undefined) || []),
      ...((summaryActions.completed_actions as Array<Record<string, any>> | undefined) || []),
      ...((summaryActions.cancelled_actions as Array<Record<string, any>> | undefined) || []),
    ];
  }, [workflowSummary]);

  const auditRows = useMemo(() => {
    if (!item) return [];
    const rows: Array<{ at: string; label: string; detail: string }> = [];
    if (item.saved_at) rows.push({ at: item.saved_at, label: 'Case opened', detail: `Safety Case saved${item.saved_by ? ` by ${item.saved_by}` : ''}.` });
    (item.recommendations || []).forEach((recommendation: any) => {
      if (recommendation.generated_at) rows.push({ at: recommendation.generated_at, label: 'Recommendation generated', detail: recommendation.recommendation_text || recommendation.original_recommendation_text || 'Recommendation recorded.' });
      if (recommendation.latest_correction?.corrected_at) rows.push({ at: recommendation.latest_correction.corrected_at, label: 'Customer correction captured', detail: recommendation.latest_correction.corrected_text || 'Correction recorded.' });
      if (recommendation.latest_decision?.decided_at) rows.push({ at: recommendation.latest_decision.decided_at, label: `Recommendation ${humanize(recommendation.latest_decision.decision_type, 'updated')}`, detail: recommendation.latest_decision.decision_reason || 'Decision recorded on the case.' });
    });
    actions.forEach((action: any) => {
      if (action.created_at) rows.push({ at: action.created_at, label: 'Corrective action recorded', detail: action.title || action.description || 'Corrective action added to the case.' });
      if (action.completed_at || action.validated_at) rows.push({ at: action.validated_at || action.completed_at, label: 'Corrective action completed', detail: operatorVisibleNote(action.verification_notes) || action.title || 'Completion evidence was recorded.' });
    });
    if (item.outcome_learning_summary?.latest_updated_at) rows.push({ at: item.outcome_learning_summary.latest_updated_at, label: 'Outcome learning refreshed', detail: (item.outcome_learning_summary.highlights || [])[0] || 'Learning state changed.' });
    return rows.sort((left, right) => String(right.at).localeCompare(String(left.at)));
  }, [actions, item]);

  const measurementReferences = useMemo(
    () => presentMeasurementReferences(operatorLifecycle?.measurement_references, {
      historyHref: '/acceptance/reports',
    }),
    [operatorLifecycle],
  );

  if (loading || !payloadMatchesCase) {
    return <Skeleton variant="rectangular" height={440} sx={{ borderRadius: 3 }} />;
  }

  if (error || !item) {
    return <Alert severity="warning">{error || 'Safety Case details are unavailable right now.'}</Alert>;
  }

  const recommendation = selectPrimaryControlRecommendation(item.recommendations || []);
  const followup = (((item as any).followup_measurement) || item.status_snapshot?.followup_measurement || {}) as Record<string, any>;
  const parsedLifecycle = parseOperatorLifecycle(operatorLifecycle);
  const overviewControl = resolveOverviewControlView({
    actions,
    recommendations: item.recommendations || [],
    lifecycleControlRecorded: parsedLifecycle?.controlRecorded,
    currentControlApplicable: parsedLifecycle?.currentControlApplicable,
  });
  const overviewFollowUp = resolveOverviewFollowUp({
    measurementStatus: item.measurement_status || followup.measurement_confidence || workflowSummary?.impact_summary?.measurement_status,
    measurementConfidence: followup.measurement_confidence,
    followupObservedCount: followup.followup_observed_count,
    directionalChange: followup.directional_change,
    rateChangePercent: workflowSummary?.impact_summary?.percent_reduction ?? followup.rate_change_percent,
    resultLabel: resolveResultLabelDisplay(deriveResultLabel({
      safetyCaseStatus: item.safety_case_status,
      impactSummary: workflowSummary?.impact_summary,
      followupMeasurement: followup,
      scope: item.scope as Record<string, unknown> | undefined,
    })),
    claimReady: parseOperatorLifecycle(operatorLifecycle)?.signoffReady,
    currentControlApplicable: parsedLifecycle?.currentControlApplicable,
    currentScanEstablished: parsedLifecycle?.currentScanEstablished,
    operationalState: parsedLifecycle?.operationalState,
  });
  const overviewControlsHref = buildSafetyCaseControlsPath({
    safetyCaseId: caseId,
    orgId: searchParams.get('org_id'),
    siteId: String(item.site_id || searchParams.get('site_id') || '').trim() || null,
    workflowId,
    incidentId: String(item.representative_incident_id || searchParams.get('incident_id') || '').trim() || null,
  });
  const patternWorkflowId = String(workflowId || item.source_pattern_id || '').trim();
  const caseSiteId = String(item.site_id || searchParams.get('site_id') || '').trim();
  const caseZoneId = String(
    item.zone_id || item.scope?.zone_id || (firstZone(item) === 'Unspecified zone' ? '' : firstZone(item)) || '',
  ).trim();
  const caseCameraId = String(item.camera_id || item.scope?.camera_id || scanCameraId || '').trim();
  const patternHref = patternWorkflowId
    ? buildSafetyCasePatternReviewPath({
      siteId: caseSiteId || null,
      workflowId: patternWorkflowId,
      incidentId: String(item.representative_incident_id || searchParams.get('incident_id') || '').trim() || null,
      zoneId: caseZoneId || null,
      cameraId: caseCameraId || null,
      deviceId: String(item.device_id || item.scope?.device_id || '').trim() || null,
      riskType: firstRisk(item) === 'Safety case' ? null : firstRisk(item),
    })
    : '';
  const overviewV2Href = caseSiteId ? buildSafetyOverviewV2Path(caseSiteId) : '';
  const signedRecordIdentity = resolveCaseSignedRecordIdentity({
    finalizedReportId: item.latest_finalized_report_id,
    latestScanCurrentRevision: (item as { latest_scan_current_revision?: unknown }).latest_scan_current_revision
      ?? (workflowSummary as { latest_scan_current_revision?: unknown } | null)?.latest_scan_current_revision,
    scanHistory: (
      (item as { scan_history?: SignedRecordRevisionSource['scanHistory'] }).scan_history
      || (workflowSummary as { scan_history?: SignedRecordRevisionSource['scanHistory'] } | null)?.scan_history
      || null
    ),
  });
  const signedRecordHref = buildExactSignedRecordPath({
    recordId: signedRecordIdentity.recordId,
    revision: signedRecordIdentity.revision,
    orgId: searchParams.get('org_id'),
    siteId: caseSiteId || null,
  });
  const prepareSignedScanHref = item.active_draft_report_id
    ? buildOverviewPrepareClaimReadinessPath({
      caseId,
      orgId: searchParams.get('org_id'),
      siteId: String(item.site_id || searchParams.get('site_id') || '').trim() || null,
      workflowId,
      incidentId: String(item.representative_incident_id || searchParams.get('incident_id') || '').trim() || null,
      reportId: String(item.active_draft_report_id || '').trim(),
    })
    : '';
  const legacyClaimBoundary = String(
    followup.validation_gates?.claim_boundary
    || item.status_snapshot?.impact_summary?.measurement_trust?.claim_boundary
    || (item as any).claim_status
    || '',
  ).trim();
  const claimBoundary = String(effectiveWorkflowClaimReadiness?.claim_boundary?.wording || '').trim()
    || legacyClaimBoundary
    || 'No formal claim boundary is recorded yet.';
  const claimReadinessOverallStatus = String(effectiveWorkflowClaimReadiness?.overall_status || '').trim().toLowerCase();
  const workplaceCondition = readWorkplaceCondition(item.scope);
  const contributingConditionStatusLabel = resolveContributingConditionStatusLabel(
    contributingPreview.contributingConditionStatus || workplaceCondition.contributing_condition_status,
  ) || humanize(workplaceCondition.contributing_condition_status, 'Not recorded');
  const scopeRecord = (item.scope && typeof item.scope === 'object') ? item.scope as Record<string, unknown> : {};
  const overviewPlace = resolveOverviewPlaceLabel({
    zoneId: firstZone(item) === 'Unspecified zone' ? '' : firstZone(item),
    zoneLabel: String(item.zone_label || scopeRecord.zone_label || ''),
    areaLabel: String(item.area_label || scopeRecord.area_label || ''),
    cameraId: String(item.camera_id || scopeRecord.camera_id || scanCameraId || ''),
    cameraLabel: String(item.camera_label || scopeRecord.camera_label || ''),
    locationLabel: scanLocationLabel,
  });
  const problemTitle = formatOverviewProblemTitle({
    riskType: firstRisk(item),
    place: overviewPlace || firstZone(item),
  });
  const inferredCta = resolveSafetyCaseDominantCta({
    canManage: canManageSafetyCases,
    hasActiveDraft: Boolean(item.active_draft_report_id),
    verifyPendingCount: operatorLifecycleReady && parsedLifecycle
      ? parsedLifecycle.reviewPending
      : verifyPendingCount,
    hasCorrectiveAction: operatorLifecycleReady && parsedLifecycle
      ? parsedLifecycle.currentControlApplicable
      : hasRecordedSafetyCaseControl({
        actions,
        recommendations: item.recommendations,
      }),
    scanCount: logicalScanCount,
  });
  const overviewNextStep = resolveOverviewNextStep({
    nextAction: parsedLifecycle?.nextAction,
    operationalState: parsedLifecycle?.operationalState,
    reviewPending: parsedLifecycle?.reviewPending,
    reviewReviewed: parsedLifecycle?.reviewReviewed,
    reviewStatus: parsedLifecycle?.reviewStatus,
  });
  const firstUnresolvedBlockerLabel = unresolvedBlockingControls
    .map((control) => customerFacingClaimReadinessLabel({
      label: control.label,
      nextActionLabel: control.next_action?.label,
      controlId: control.control_id,
    }))
    .find(Boolean) || null;
  const nextSafetyScan = resolveNextSafetyScanView({
    nextAction: parsedLifecycle?.nextAction,
    operationalState: parsedLifecycle?.operationalState,
    reviewPending: parsedLifecycle?.reviewPending,
    hasActiveDraft: Boolean(item.active_draft_report_id),
    hasSignedScan: Boolean(item.latest_finalized_report_id),
    signoffReady: parsedLifecycle?.signoffReady,
    claimReadinessResolved: parsedLifecycle?.claimReadinessResolved,
    claimReadinessTotal: parsedLifecycle?.claimReadinessTotal,
    firstUnresolvedBlockerLabel,
  });
  const reviewCompleteNoWork = Boolean(
    parsedLifecycle
    && parsedLifecycle.nextAction === 'none'
    && parsedLifecycle.reviewPending === 0
    && overviewNextStep.kind === 'review_complete',
  );
  const openSafetyScanHref = (buyerCustomerScanReportId || item.active_draft_report_id)
    ? buildScanVerifyPath({
      reportId: String(buyerCustomerScanReportId || item.active_draft_report_id),
      orgId: searchParams.get('org_id') || item.org_id,
      siteId: String(item.site_id || searchParams.get('site_id') || '').trim() || null,
      safetyCaseId: String(item.safety_case_id || item.finding_id || caseId || '').trim() || null,
    })
    : '';
  const dominantCta = (!operatorLifecycleReady && lifecycleReportId)
    ? {
      kind: 'none' as const,
      label: 'Monitoring',
      tab: 'overview' as const,
      presentation: 'status' as const,
    }
    : resolveOverviewCtaFromLifecycle(parsedLifecycle, inferredCta, canManageSafetyCases);
  const buyerCaseState = mapBuyerCaseState({
    controlStage: mapControlStage({
      safetyCaseStatus: item.safety_case_status,
      safetyCaseStatusLabel: item.safety_case_status_label,
      customerControlStage: parsedLifecycle?.operationalState,
    }),
    hasNewActivity: hasNewActivitySinceSignedScan(item),
    scanCount: logicalScanCount,
  });
  const handleDominantCta = () => {
    if (overviewNextStep.primaryCta?.kind === 'verify_observations' || dominantCta.kind === 'verify_observations') {
      openVerifyObservations();
      return;
    }
    if (overviewNextStep.primaryCta?.kind === 'establish_initial_baseline' || dominantCta.kind === 'establish_initial_baseline') {
      void establishMeasurementReference('initial_baseline');
      return;
    }
    if (dominantCta.presentation !== 'action') return;
    setActiveTab(dominantCta.tab);
  };
  const scanRecordsQuiet = dominantCta.kind !== 'verify_observations';
  const itemRecord = item as Record<string, unknown>;
  const summaryRecord = (workflowSummary || null) as Record<string, unknown> | null;
  const activeDraftWindow = resolveActiveDraftScanWindow({
    activeDraftReportId: item.active_draft_report_id,
    scanHistory: itemRecord.scan_history || summaryRecord?.scan_history,
    firstEligibleScan: itemRecord.first_eligible_scan || summaryRecord?.first_eligible_scan,
    reportOpenWindow: scanReportAnchorWindow,
  });
  const scanWindowLabel = (activeDraftWindow.start || activeDraftWindow.end)
    ? formatHumanScanWindow(activeDraftWindow.start, activeDraftWindow.end, siteTimeZone)
    : '';
  const latestEvidence = formatOverviewLatestEvidence({
    confirmedCount: parsedLifecycle?.reviewConfirmed ?? scanProgress?.confirmed_count,
    reviewedCount: parsedLifecycle?.reviewReviewed ?? scanProgress?.reviewed_count,
    candidateCount: scanProgress?.candidate_episode_count,
    pendingCount: parsedLifecycle?.reviewPending ?? null,
    linkedReportCount: item.linked_report_count,
    riskType: firstRisk(item),
    hasActiveDraft: Boolean(item.active_draft_report_id),
  });
  const operationalSummary = formatOverviewProblemSummary({
    riskType: firstRisk(item),
    hazardCategory: item.hazard_category,
    place: overviewPlace || firstZone(item),
    conditionType: workplaceCondition.condition_type,
    conditionSummary: workplaceCondition.condition_summary,
    likelyContributing: workplaceCondition.likely_contributing_condition,
    conditionTags: workplaceCondition.condition_tags || undefined,
  });
  const moreAccordionSx = { border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } };

  return (
    <Stack
      spacing={3}
      sx={{ width: '100%', maxWidth: SAFETY_CASE_DETAIL_MAX_WIDTH_PX, mx: 'auto', pb: 2 }}
      data-testid="safety-case-detail-shell"
    >
      <Breadcrumbs
        aria-label="Safety Case parentage"
        data-testid="safety-case-breadcrumbs"
        sx={{ '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap' } }}
      >
        <Link
          component={RouterLink}
          to={buildSafetyManagementWorkspaceHref(searchParams)}
        >
          Safety Management
        </Link>
        <Link
          component={RouterLink}
          to={buildSafetyManagementWorkspaceHref(searchParams, { tab: 'cases' })}
        >
          Safety Cases
        </Link>
        <Typography color="text.primary">{problemTitle}</Typography>
      </Breadcrumbs>
      <Box sx={{ maxWidth: '42rem' }}>
        <Typography variant="overline" color="text.secondary">Safety Case</Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75, letterSpacing: '-0.01em' }}>
          {problemTitle}
        </Typography>
        <Typography color="text.secondary" data-testid="safety-case-operational-summary">
          {operationalSummary}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.25 }}>
          <Chip size="small" color={buyerCaseState === 'Closed' ? 'success' : 'warning'} label={buyerCaseState} />
          <Chip size="small" variant="outlined" label={humanize(firstRisk(item))} />
        </Stack>
        {(patternHref || overviewV2Href) ? (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 1 }}
            data-testid="safety-case-workflow-nav"
          >
            {patternHref ? (
              <Button
                component={RouterLink}
                to={patternHref}
                size="small"
                variant="text"
                data-testid="safety-case-view-pattern"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                View pattern
              </Button>
            ) : null}
            {overviewV2Href ? (
              <Button
                component={RouterLink}
                to={overviewV2Href}
                size="small"
                variant="text"
                data-testid="safety-case-back-to-overview"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Back to safety overview
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Box>

      <SafetyCaseHomeStatusStrip
        facts={{
          scanWindowLabel,
          hasActiveDraft: Boolean(item.active_draft_report_id),
          currentScanHref: openSafetyScanHref || '',
          hasSignedScan: Boolean(item.latest_finalized_report_id),
          latestSignedHref: signedRecordHref,
          ...scheduledScanFacts,
          controlTitle: overviewControl.title,
          controlMissing: overviewControl.case === 'missing',
          controlNoneInForce: overviewControl.case === 'none_in_force',
          hasNewActivity: hasNewActivitySinceSignedScan(item),
          primaryCtaLabel: dominantCta.label,
          primaryCtaIsAction: dominantCta.presentation === 'action',
          primaryCtaKind: dominantCta.kind,
          scanCount: logicalScanCount || null,
          controlOwner: String(actions[0]?.assignee || '').trim() || null,
        }}
        onPrimaryCta={handleDominantCta}
      />

      {logicalScanCount > 1 ? <FirstSafetyScanFindingCard view={firstScanFinding} /> : null}

      <SafetyCaseOwnershipSection
        safetyCaseId={String(item.safety_case_id || item.finding_id || caseId)}
        owner={item.followup_owner}
        hasFollowupOwner={item.has_followup_owner}
        assignedAt={item.followup_owner_assigned_at}
        assignedBy={item.followup_owner_assigned_by}
        canAssign={canAssignOwner}
        onAssigned={() => { void loadCase(caseId, { silent: true }); }}
      />

      {!canManageSafetyCases ? (
        <Alert severity="info">
          Read-only Safety Case access. View recommendations, evidence, and signed scans; assign, close, sign, and scan mutations are hidden.
        </Alert>
      ) : null}

      <Tabs
        value={activeTab}
        onChange={(_event, next: SafetyCaseDetailTab) => setActiveTab(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        aria-label="Safety Case sections"
        data-testid="safety-case-tabs"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {SAFETY_CASE_DETAIL_TABS.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={tab.label}
            data-testid={`safety-case-tab-${tab.id}`}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          />
        ))}
      </Tabs>

      {activeTab === 'overview' ? (
        <Stack spacing={2}>
          <CaseHistorySection
            events={timelineEvents}
            auditRows={auditRows}
            formatWhen={(value, fallback) => formatDateTime(value, fallback, siteTimeZone)}
            heading="Case history"
            emptyCopy="Case lifecycle events will appear here as persisted history is recorded."
            testId="safety-case-persisted-timeline"
            eventTestId="safety-case-timeline-event"
            density="summary"
          />
        <SafetyCaseOverviewPanels
          operatorLifecycleReady={operatorLifecycleReady}
          lifecycleReportId={lifecycleReportId}
          parsedLifecycle={parsedLifecycle}
          overviewNextStep={overviewNextStep}
          nextSafetyScan={nextSafetyScan}
          canManageSafetyCases={canManageSafetyCases}
          onDominantCta={handleDominantCta}
          openSafetyScanHref={openSafetyScanHref}
          prepareSignedScanHref={prepareSignedScanHref}
          showPrepareSignedScan={Boolean(
            parsedLifecycle?.signoffReady && prepareSignedScanHref && !reviewCompleteNoWork,
          )}
          latestEvidence={latestEvidence}
          evidenceImageUrl={evidenceImageUrl}
          hasActiveDraft={Boolean(item.active_draft_report_id)}
          hasSignedScan={Boolean(item.latest_finalized_report_id)}
          previousReportId={String(item.previous_report_id || '').trim() || null}
          onViewPreviousSafetyScan={item.previous_report_id ? () => openPreviousScan() : undefined}
          overviewPlace={overviewPlace}
          scanWindowLabel={scanWindowLabel}
          scanRecordsQuiet={scanRecordsQuiet}
          onViewSafetyScan={() => {
            const id = String(item.active_draft_report_id || item.latest_finalized_report_id || '').trim();
            if (id) openSelectedScan(id);
          }}
          onReviewEvidence={openVerifyObservations}
          showReviewEvidence={Boolean(
            canManageSafetyCases
            && item.active_draft_report_id
            && dominantCta.kind !== 'verify_observations'
            && !reviewCompleteNoWork,
          )}
          overviewControl={overviewControl}
          overviewControlsHref={overviewControlsHref}
          onRecordControl={() => setActiveTab('controls')}
          showRecordControlCta={Boolean(
            overviewControl.showRecordControlCta
            && dominantCta.kind !== 'record_control'
            && dominantCta.kind !== 'establish_initial_baseline',
          )}
          overviewFollowUp={overviewFollowUp}
          signedRecordHref={signedRecordHref}
          measurementReferences={measurementReferences}
          onEstablishInitialBaseline={() => { void establishMeasurementReference('initial_baseline'); }}
          onEstablishControlReference={() => { void establishMeasurementReference('control_reference'); }}
        />
        </Stack>
      ) : null}

      {activeTab === 'verify' ? (
        <CaseVerifyPanel
          events={timelineEvents}
          currentScanId={item.active_draft_report_id || null}
          latestSignedId={item.latest_finalized_report_id || null}
          siteTimeZone={siteTimeZone}
          currentScanPendingCount={parsedLifecycle?.reviewPending ?? verifyPendingCount}
          controlRevisionId={String(
            actions.find((action: any) => action.control_revision_id || action.implemented_at)?.control_revision_id
            || actions.find((action: any) => action.implemented_at)?.id
            || '',
          ).trim() || null}
          implementedAt={String(
            actions.find((action: any) => action.implemented_at)?.implemented_at || '',
          ).trim() || null}
          onOpenScan={openSelectedScan}
          onOpenCurrentScan={openVerifyObservations}
          onViewSignedRecord={(reportId) => viewSignedScan(reportId)}
        />
      ) : null}

      {activeTab === 'controls' ? (
        <Stack spacing={2}>
          <ControlEvaluationPanel safetyCaseId={caseId} />
          <SafetyCaseRecommendationsSection
          safetyCaseId={caseId}
          workflowId={workflowId}
          recommendations={item.recommendations || []}
          recordedActions={actions}
          latestFinalizedReportId={item.latest_finalized_report_id}
          workplaceCondition={workplaceCondition}
          siteTimeZone={siteTimeZone}
          onCaseRefresh={() => loadCase(caseId, { silent: true })}
          onContinueMonitoring={() => setActiveTab('overview')}
          readOnly={!canManageSafetyCases}
          hasControl={hasRecordedSafetyCaseControl({
            actions,
            recommendations: item.recommendations,
          })}
          followupObservedCount={followup.followup_observed_count}
          directionalChange={followup.directional_change}
          rateChangePercent={workflowSummary?.impact_summary?.percent_reduction ?? followup.rate_change_percent}
          measurementStatus={item.measurement_status || followup.measurement_confidence || workflowSummary?.impact_summary?.measurement_status}
          measurementConfidence={followup.measurement_confidence}
          correctiveActionsSlot={(
            <Card variant="outlined" sx={{ borderRadius: 2 }} data-testid="safety-case-corrective-actions">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Recorded controls</Typography>
                {actions.length ? actions.map((action: any, index: number) => (
                  <Box key={String(action.created_at || action.title || index)} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{action.title || action.description || 'Control'}</Typography>
                    <Typography variant="body2" color="text.secondary">Control owner: {action.assignee || 'Unassigned'} · Status: {humanize(action.status)} · Due: {formatDateTime(action.due_date, 'Not recorded', siteTimeZone)}</Typography>
                    <Typography variant="body2" color="text.secondary">Evidence count: {Number(action.evidence_count || 0)} · Verification: {operatorVisibleNote(action.verification_notes) || action.verification_method || 'Pending'}</Typography>
                  </Box>
                )) : <Typography variant="body2" color="text.secondary">No controls are recorded on this case yet.</Typography>}
              </CardContent>
            </Card>
          )}
          followUpSlot={(
            <Card variant="outlined" sx={{ borderRadius: 2 }} data-testid="safety-case-followup-measurement">
              <CardContent>
                {hasCanonicalClaimReadiness ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Workflow measurement status: {humanize(workflowSummary?.impact_summary?.measurement_status || workflowSummary?.impact_summary?.status, 'Pending')}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary">Measurement status: {humanize(item.measurement_status || followup.measurement_confidence, 'Pending')}</Typography>
                <Typography variant="body2" color="text.secondary">Baseline observed: {followup.baseline_observed_count ?? 'n/a'} · Follow-up observed: {followup.followup_observed_count ?? 'n/a'}</Typography>
                <Typography variant="body2" color="text.secondary">Directional change: {humanize(followup.directional_change, 'Not measured')} {followup.rate_change_percent != null ? `(${Number(followup.rate_change_percent)}%)` : ''}</Typography>
                <Typography variant="body2" color="text.secondary">Next scan guidance: {item.status_snapshot?.impact_summary?.recommended_next_step ? humanize(item.status_snapshot.impact_summary.recommended_next_step) : 'Open the next Safety Scan when you need to package this updated case state.'}</Typography>
              </CardContent>
            </Card>
          )}
        />
        </Stack>
      ) : null}

      {activeTab === 'more' ? (
        <Stack spacing={1.5} data-testid="safety-case-more-sections">
          <Accordion
            disableGutters
            elevation={0}
            expanded={moreSection === 'condition'}
            onChange={(_event, expanded) => setMoreSection(expanded ? 'condition' : 'condition')}
            data-testid="more-section-condition"
            sx={moreAccordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Condition review</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <WorkplaceConditionSummaryCard
                  safetyCaseId={caseId}
                  item={item}
                  workflowSummary={workflowSummary}
                  contributingPreview={contributingPreview}
                  hideSaveButton={useUnifiedAttestationSave}
                  saveHandlerRef={workplaceConditionSaveRef}
                  onSaved={() => {
                    void loadCase(caseId);
                  }}
                />
                <Box ref={contributingConditionReviewRef}>
                  <ContributingConditionReviewSection
                    safetyCaseId={caseId}
                    item={item}
                    readOnly={!canManageSafetyCases}
                    hideSaveButton={useUnifiedAttestationSave}
                    saveHandlerRef={contributingConditionSaveRef}
                    onPreviewChange={setContributingPreview}
                    onSaved={() => {
                      void loadCase(caseId);
                    }}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            elevation={0}
            expanded={moreSection === 'claim'}
            onChange={(_event, expanded) => setMoreSection(expanded ? 'claim' : 'condition')}
            data-testid="more-section-claim"
            sx={moreAccordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Claim readiness</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } }}>
                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Risk pattern</Typography>
                      <Typography variant="body2" color="text.secondary">{recommendation?.reason || item.source_pattern_id || 'This case tracks a recurring exposure pattern confirmed in review.'}</Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Claim boundary</Typography>
                      <Typography variant="body2" color="text.secondary">{claimBoundary}</Typography>
                      {hasCanonicalClaimReadiness ? (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={claimReadinessOverallStatus === 'ready' ? 'success' : 'warning'}
                            label={`Overall: ${humanize(claimReadinessOverallStatus || 'blocked', 'Blocked')}`}
                          />
                          {effectiveWorkflowClaimReadiness?.claim_boundary?.outcome_claimed ? (
                            <Chip size="small" variant="outlined" label="Outcome claim allowed" />
                          ) : null}
                          {effectiveWorkflowClaimReadiness?.claim_boundary?.exposure_reduction_claimed ? (
                            <Chip size="small" variant="outlined" label="Exposure reduction claim allowed" />
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Claim status: {humanize((item as any).claim_status, 'Not claimed')}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>

                {hasCanonicalClaimReadiness ? (
                  <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-claim-readiness-summary">
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Claim readiness</Typography>
                      {claimReadinessSummaryLabel ? (
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={unresolvedBlockingControls.length > 0 ? 'warning' : 'success'}
                            label={claimReadinessSummaryLabel}
                          />
                          {unresolvedBlockingControls.length > 0 ? (
                            <Chip size="small" color="warning" label="Claim readiness blocked" />
                          ) : null}
                        </Stack>
                      ) : null}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Canonical workflow controls that determine whether the next Safety Scan can support a defensible claim.
                      </Typography>
                      <Stack spacing={1.25}>
                        {claimReadinessControls.map((control) => (
                          <Box key={String(control.control_id || control.label)}>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {customerFacingClaimReadinessLabel({
                                  label: control.label,
                                  nextActionLabel: control.next_action?.label,
                                  controlId: control.control_id,
                                })}
                              </Typography>
                              <Chip
                                size="small"
                                color={controlStatusColor(control.status)}
                                label={controlStatusLabel(control.status)}
                              />
                              {control.blocking === false ? (
                                <Chip size="small" variant="outlined" label="Non-blocking" />
                              ) : null}
                            </Stack>
                            {control.next_action?.label ? (
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Next action: {customerFacingClaimReadinessLabel({
                                    label: control.next_action.label,
                                    controlId: control.control_id,
                                  })}
                                </Typography>
                                {canManageSafetyCases && isUnresolvedBlockingControl(control) ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => focusClaimReadinessBlocker(String(control.control_id || ''))}
                                  >
                                    {claimReadinessBlockerActionLabel(String(control.control_id || ''))}
                                  </Button>
                                ) : null}
                              </Stack>
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                      {unresolvedBlockingControls.length > 0 ? (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                          {unresolvedBlockingControls.length} blocking control{unresolvedBlockingControls.length === 1 ? '' : 's'} still need resolution before the next scan can claim effectiveness.
                        </Alert>
                      ) : (
                        <Alert severity="success" sx={{ mt: 1.5 }}>
                          All blocking claim-readiness controls are resolved for this workflow.
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                ) : null}

                {workflowId ? (
                  <ClaimReadinessValidationSection
                    workflowId={workflowId}
                    reportId={draftReportId}
                    safetyCaseId={caseId}
                    readOnly={!canManageSafetyCases}
                    refreshKey={`${item.updated_at || ''}:${effectiveWorkflowClaimReadiness?.resolved_count || 0}`}
                    onFocusBlockerType={focusClaimReadinessBlocker}
                    sectionTitle="Claim readiness validation"
                    sectionSubtitle="Clear live workflow blockers as auditable validation tasks. Completed validation strengthens the next Safety Scan without changing any signed scan artifact."
                    startPromptText={
                      unresolvedBlockingControls.length
                        ? `${unresolvedBlockingControls.length} blocking control${unresolvedBlockingControls.length === 1 ? '' : 's'} can be assigned and cleared here before the next scan sign-off.`
                        : ''
                    }
                  />
                ) : null}
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            elevation={0}
            expanded={moreSection === 'attestation'}
            onChange={(_event, expanded) => setMoreSection(expanded ? 'attestation' : 'condition')}
            data-testid="more-section-attestation"
            sx={moreAccordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Attestation</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box ref={closureSummaryRef}>
                  <ClosureSummarySection
                    safetyCaseId={caseId}
                    item={item}
                    readOnly={!canManageSafetyCases}
                    hideSaveButton={useUnifiedAttestationSave}
                    saveHandlerRef={closureSummarySaveRef}
                    workflowImpactSummary={workflowSummary?.impact_summary as Record<string, unknown> | undefined}
                    onSaved={() => {
                      void loadCase(caseId);
                    }}
                  />
                </Box>
                <FollowUpResultSection
                  safetyCaseId={caseId}
                  item={item}
                  readOnly={!canManageSafetyCases}
                  hideSaveButton={useUnifiedAttestationSave}
                  saveHandlerRef={followUpResultSaveRef}
                  workflowImpactSummary={workflowSummary?.impact_summary as Record<string, unknown> | undefined}
                  followupMeasurement={followup}
                  onSaved={() => {
                    void loadCase(caseId);
                  }}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Accordion
            disableGutters
            elevation={0}
            expanded={moreSection === 'audit'}
            onChange={(_event, expanded) => setMoreSection(expanded ? 'audit' : 'condition')}
            data-testid="more-section-audit"
            sx={moreAccordionSx}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>Audit history</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Alert severity="info" data-testid="safety-case-signoff-help">
                  Sign-off freezes the Safety Scan, not the Safety Case.
                </Alert>
                <CaseHistorySection
                  events={timelineEvents}
                  auditRows={auditRows}
                  formatWhen={(value, fallback) => formatDateTime(value, fallback, siteTimeZone)}
                  heading="Case history"
                  emptyCopy="Case lifecycle events will appear here as the case history grows."
                  testId="case-history-more-audit"
                  density="full"
                />
                <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-advanced-ids">
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Identifiers</Typography>
                    <Typography variant="body2" color="text.secondary">Safety Case: {item.safety_case_id || item.finding_id || caseId}</Typography>
                    <Typography variant="body2" color="text.secondary">Workflow: {workflowId || 'Not recorded'}</Typography>
                    <Typography variant="body2" color="text.secondary">Active draft scan: {item.active_draft_report_id || 'None'}</Typography>
                    <Typography variant="body2" color="text.secondary">Previous scan: {item.previous_report_id || 'None'}</Typography>
                    <Typography variant="body2" color="text.secondary">Latest signed scan: {item.latest_finalized_report_id || 'None'}</Typography>
                    <Typography variant="body2" color="text.secondary">Representative incident: {item.representative_incident_id || 'Not attached'}</Typography>
                    <Typography variant="body2" color="text.secondary">First seen: {formatDateTime(item.created_at, 'Not recorded', siteTimeZone)} · Last updated: {formatDateTime(item.updated_at, 'Not recorded', siteTimeZone)}</Typography>
                    <Typography variant="body2" color="text.secondary">Review: {item.review_status?.status_label || item.review_status?.status || 'Not reviewed yet'}</Typography>
                    <Typography variant="body2" color="text.secondary">Contributing condition review: {contributingConditionStatusLabel}</Typography>
                  </CardContent>
                </Card>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {useUnifiedAttestationSave ? (
            <SafetyCaseAttestationSaveBar
              saving={attestationSaving}
              error={attestationSaveError}
              notice={attestationSaveNotice}
              onSave={() => { void handleSaveAllAttestation(); }}
            />
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default SafetyCaseDetailPage;
